/**
 * ProfileCombobox Component
 * Dropdown/combobox for selecting profiles with search functionality
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Profile } from "../services/api";

interface ProfileComboboxProps {
    profiles: Profile[];
    selectedProfile?: Profile | null;
    onProfileSelect?: (profile: Profile | null) => void;
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
}

export const ProfileCombobox: React.FC<ProfileComboboxProps> = ({
    profiles,
    selectedProfile,
    onProfileSelect,
    placeholder = "Select a profile...",
    className,
    style,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Handle clicking outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setSearchQuery("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const filteredProfiles = profiles.filter((profile) =>
        profile.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleProfileSelect = useCallback(
        (profile: Profile) => {
            onProfileSelect?.(profile);
            setIsOpen(false);
            setSearchQuery("");
        },
        [onProfileSelect]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (!isOpen) {
            setIsOpen(true);
        }
    };

    const handleInputFocus = () => {
        if (!disabled) {
            setIsOpen(true);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            setIsOpen(false);
            setSearchQuery("");
            inputRef.current?.blur();
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!isOpen) {
                setIsOpen(true);
            }
        }
    };

    const displayValue = selectedProfile?.name || "";

    // Styles
    const containerStyle: React.CSSProperties = {
        position: "relative",
        width: "100%",
        ...style,
    };

    const inputContainerStyle: React.CSSProperties = {
        position: "relative",
        display: "flex",
        alignItems: "center",
    };

    const inputStyle: React.CSSProperties = {
        width: "100%",
        padding: "8px 12px",
        paddingRight: "32px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        fontSize: "14px",
        backgroundColor: disabled ? "#f9fafb" : "white",
        color: disabled ? "#9ca3af" : "#1f2937",
        cursor: disabled ? "not-allowed" : "text",
        outline: "none",
        transition: "border-color 0.2s",
    };

    const chevronStyle: React.CSSProperties = {
        position: "absolute",
        right: "8px",
        top: "50%",
        transform: `translateY(-50%) rotate(${isOpen ? "180deg" : "0deg"})`,
        transition: "transform 0.2s",
        pointerEvents: "none",
        color: "#6b7280",
    };

    const dropdownStyle: React.CSSProperties = {
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 50,
        backgroundColor: "white",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        maxHeight: "200px",
        overflowY: "auto",
        marginTop: "4px",
    };

    const optionStyle: React.CSSProperties = {
        padding: "8px 12px",
        cursor: "pointer",
        borderBottom: "1px solid #f3f4f6",
        transition: "background-color 0.2s",
    };

    const selectedOptionStyle: React.CSSProperties = {
        ...optionStyle,
        backgroundColor: "#eff6ff",
        borderLeft: "3px solid #3b82f6",
    };

    const profileNameStyle: React.CSSProperties = {
        fontWeight: 500,
        fontSize: "14px",
        color: "#1f2937",
        marginBottom: "2px",
    };

    const profileMetaStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
    };

    const emptyStateStyle: React.CSSProperties = {
        padding: "12px",
        textAlign: "center",
        color: "#6b7280",
        fontSize: "14px",
    };

    return (
        <div ref={dropdownRef} className={className} style={containerStyle}>
            <div style={inputContainerStyle}>
                <input
                    ref={inputRef}
                    type="text"
                    value={isOpen ? searchQuery : displayValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={inputStyle}
                    onMouseEnter={(e) => {
                        if (!disabled) {
                            e.currentTarget.style.borderColor = "#9ca3af";
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!disabled && !isOpen) {
                            e.currentTarget.style.borderColor = "#d1d5db";
                        }
                    }}
                    onFocus={(e) => {
                        handleInputFocus();
                        if (!disabled) {
                            e.currentTarget.style.borderColor = "#3b82f6";
                        }
                    }}
                    onBlur={(e) => {
                        if (!disabled) {
                            e.currentTarget.style.borderColor = "#d1d5db";
                        }
                    }}
                />
                <div style={chevronStyle}>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            </div>

            {isOpen && (
                <div style={dropdownStyle}>
                    {filteredProfiles.length === 0 && (
                        <div style={emptyStateStyle}>
                            {searchQuery
                                ? `No profiles found for "${searchQuery}"`
                                : "No profiles available"}
                        </div>
                    )}

                    {filteredProfiles.map((profile, index) => (
                        <div
                            key={profile.id}
                            style={
                                selectedProfile?.id === profile.id
                                    ? selectedOptionStyle
                                    : optionStyle
                            }
                            onClick={() => handleProfileSelect(profile)}
                            onMouseEnter={(e) => {
                                if (selectedProfile?.id !== profile.id) {
                                    e.currentTarget.style.backgroundColor =
                                        "#f9fafb";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedProfile?.id !== profile.id) {
                                    e.currentTarget.style.backgroundColor =
                                        "white";
                                } else {
                                    e.currentTarget.style.backgroundColor =
                                        "#eff6ff";
                                }
                            }}
                        >
                            <div style={profileNameStyle}>
                                {profile.name}
                                {index === 0 && (
                                    <span
                                        style={{
                                            marginLeft: "6px",
                                            fontSize: "10px",
                                            backgroundColor: "#3b82f6",
                                            color: "white",
                                            padding: "1px 4px",
                                            borderRadius: "3px",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Main
                                    </span>
                                )}
                            </div>
                            <div style={profileMetaStyle}>
                                Created{" "}
                                {new Date(
                                    profile.created_at
                                ).toLocaleDateString()}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
